/* Puff Land – JavaScript du thème (vanilla, Web Components, sans dépendance) */
(() => {
  'use strict';

  const PL = window.PL || { routes: {}, settings: {}, strings: {} };
  const R = PL.routes;
  const S = PL.strings;
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const storage = {
    get(key, fallback) {
      try { const v = localStorage.getItem(key); return v === null ? fallback : JSON.parse(v); } catch (e) { return fallback; }
    },
    set(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); return true; } catch (e) { return false; }
    }
  };

  /* ---------- Monnaie ---------- */
  const formatMoney = (cents, format = PL.settings.moneyFormat || '{{amount_with_comma_separator}} €') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = (n, decimals, thousands = ',', dec = '.') => {
      if (isNaN(n) || n == null) return '0';
      const num = (n / 100).toFixed(decimals);
      const parts = num.split('.');
      const ints = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? ints + dec + parts[1] : ints;
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return format;
    let value;
    switch (match[1]) {
      case 'amount': value = fmt(cents, 2); break;
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  };

  /* ---------- Toasts ---------- */
  const toast = (message, type = 'success', action) => {
    const region = $('#ToastRegion');
    if (!region || !message) return;
    const el = document.createElement('div');
    el.className = `toast toast--${type}`;
    el.setAttribute('role', type === 'error' ? 'alert' : 'status');
    const icon = type === 'error' ? '<path d="M12 3.5l9.5 16.5h-19z"/><path d="M12 10v4.5M12 17.2v.1"/>' : '<circle cx="12" cy="12" r="9"/><path d="M8 12.5l3 3 5-6"/>';
    el.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${icon}</svg><span class="toast__msg"></span>`;
    el.querySelector('.toast__msg').textContent = message;
    if (action) {
      const a = document.createElement(action.href ? 'a' : 'button');
      a.className = 'toast__action';
      a.textContent = action.label;
      if (action.href) a.href = action.href; else a.addEventListener('click', action.onClick);
      el.appendChild(a);
    }
    const close = document.createElement('button');
    close.className = 'toast__close';
    close.type = 'button';
    close.setAttribute('aria-label', S.close || 'Fermer');
    close.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    el.appendChild(close);
    region.appendChild(el);
    const remove = () => { el.classList.add('is-leaving'); setTimeout(() => el.remove(), 260); };
    close.addEventListener('click', remove);
    setTimeout(remove, PL.settings.toastDuration || 4000);
  };
  PL.toast = toast;

  /* ---------- Gestion du focus et du défilement ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = $$(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  class PLDrawer extends HTMLElement {
    open(opener) {
      this.opener = opener || document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('has-overlay');
        const panel = $('.drawer__panel', this);
        const focusPanel = () => { if (panel && this.classList.contains('is-open') && !panel.contains(document.activeElement)) panel.focus(); };
        focusPanel();
        /* Panneau pas encore visible (transition de visibilité) : nouvel essai aux images suivantes */
        if (panel && document.activeElement !== panel) requestAnimationFrame(() => requestAnimationFrame(focusPanel));
      });
      this.releaseTrap = trapFocus(this);
      this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
      document.addEventListener('keydown', this.onKey);
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('has-overlay');
      if (this.releaseTrap) this.releaseTrap();
      document.removeEventListener('keydown', this.onKey);
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      setTimeout(() => { if (!this.classList.contains('is-open')) this.hidden = true; }, 340);
      if (this.opener && this.opener.focus) this.opener.focus();
    }
  }

  class MenuDrawer extends PLDrawer {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-menu-close]')) this.close(); });
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  class CartDrawer extends PLDrawer {
    connectedCallback() {
      this.addEventListener('click', (e) => { if (e.target.closest('[data-cart-close]')) this.close(); });
    }
    open(opener) {
      super.open(opener);
      if (PL.fillCartRecos) PL.fillCartRecos(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /* Panneau « Catalogue » mobile (méga-menu ordinateur, ou univers en onglets : snippets/catalog-univers) */
  class CatalogSheet extends PLDrawer {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-catalog-close]')) { this.close(); return; }
        const tab = e.target.closest('.cat-u__tab');
        if (tab) this.selectTab(tab);
      });
      this.addEventListener('keydown', (e) => {
        const tab = e.target.closest('.cat-u__tab');
        if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
        const tabs = $$('.cat-u__tab', this);
        const i = tabs.indexOf(tab);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        e.preventDefault();
        this.selectTab(tabs[next]);
        tabs[next].focus();
      });
    }

    selectTab(tab) {
      $$('.cat-u__tab', this).forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }
  customElements.define('catalog-sheet', CatalogSheet);

  document.addEventListener('click', (e) => {
    const menuBtn = e.target.closest('[data-menu-open]');
    if (menuBtn) { const d = $('#MenuDrawer'); if (d) d.open(menuBtn); return; }
    const cartBtn = e.target.closest('[data-cart-open]');
    if (cartBtn && $('#CartDrawer')) { e.preventDefault(); $('#CartDrawer').open(cartBtn); return; }
    const catBtn = e.target.closest('[data-catalog-open]');
    if (catBtn && $('#CatalogSheet')) { e.preventDefault(); $('#CatalogSheet').open(catBtn); }
  });

  /* ---------- Panier (Cart API + Section Rendering API) ---------- */
  const cartSections = () => {
    const ids = [];
    if ($('#CartDrawer')) ids.push('cart-drawer');
    const main = $('#MainCart');
    if (main) ids.push(main.dataset.sectionId);
    return ids;
  };

  const updateCartCount = (count) => {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    if (sections['cart-drawer']) {
      const doc = parseHTML(sections['cart-drawer']);
      const fresh = $('.drawer__panel', doc);
      const current = $('#CartDrawer .drawer__panel');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }
    const main = $('#MainCart');
    if (main && sections[main.dataset.sectionId]) {
      const doc = parseHTML(sections[main.dataset.sectionId]);
      const fresh = $('#MainCartContent', doc);
      const current = $('#MainCartContent');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }
    if (PL.loyalty) PL.loyalty.render();
    const drawer = $('#CartDrawer');
    if (drawer && !drawer.hidden) fillCartRecos(drawer);
    if (main) fillCartRecos(main);
  };

  /* Suggestions du panier : complétées par les recommandations complémentaires de Shopify (section cart-recos)
     quand les produits compatibles et la collection de repli ne suffisent pas. Chargées à l'ouverture du tiroir. */
  const recoCache = {};
  const fillCartRecos = async (ctx = document) => {
    const boxes = $$('[data-cart-upsell][data-recos-url]', ctx);
    for (const box of boxes) {
      const limit = Number(box.dataset.limit || 3);
      const list = $('[data-upsell-list]', box);
      if (!list || $$('[data-upsell-item]', list).length >= limit) continue;
      const url = box.dataset.recosUrl;
      if (!recoCache[url]) recoCache[url] = fetch(url).then((r) => (r.ok ? r.text() : '')).catch(() => '');
      const html = await recoCache[url];
      if (!html) continue;
      const skip = String(box.dataset.inCart || '').split(',').filter(Boolean);
      $$('[data-cart-recos] [data-upsell-item]', parseHTML(html)).forEach((li) => {
        const id = li.dataset.productId;
        if ($$('[data-upsell-item]', list).length >= limit || skip.includes(id) || $(`[data-upsell-item][data-product-id="${id}"]`, list)) return;
        list.appendChild(document.importNode(li, true));
      });
      if ($('[data-upsell-item]', list)) box.hidden = false;
    }
  };
  PL.fillCartRecos = fillCartRecos;

  const request = async (url, body) => {
    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body)
      });
    } catch (err) {
      const e = new Error(S.network); e.network = true; throw e;
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const e = new Error(data.description || data.message || S.error);
      e.status = res.status; throw e;
    }
    return data;
  };

  const getCart = () => fetch(`${R.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

  const refreshCart = async () => {
    const ids = cartSections();
    const cart = await getCart();
    updateCartCount(cart.item_count);
    if (ids.length) {
      const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      renderCartSections(await res.json());
    }
    return cart;
  };

  const Cart = {
    busy: false,
    async add(items, opener) {
      const data = await request(`${R.cartAdd}.js`, { items, sections: cartSections(), sections_url: window.location.pathname });
      renderCartSections(data.sections);
      const cart = await getCart();
      updateCartCount(cart.item_count);
      const drawer = $('#CartDrawer');
      if (drawer && PL.settings.cartType === 'drawer') drawer.open(opener);
      else toast(S.added, 'success', { label: S.viewCart, href: R.cart });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
      return cart;
    },
    async change(line, quantity) {
      const data = await request(`${R.cartChange}.js`, { line, quantity, sections: cartSections(), sections_url: window.location.pathname });
      renderCartSections(data.sections);
      updateCartCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
      const item = data.items && data.items[line - 1];
      if (quantity > 0 && item && item.quantity < quantity) toast(S.soldOut, 'error');
      return data;
    },
    async clear() {
      await request(`${R.cartClear}.js`, {});
      await refreshCart();
      toast(S.cartCleared, 'info');
    },
    async setDiscounts(codes) {
      return request(`${R.cartUpdate}.js`, { discount: codes.join(',') });
    }
  };
  PL.cart = Cart;

  /* Changement de saveur d'une ligne (snippets/cart-lines) : ajout de la variante choisie (même quantité, mêmes propriétés),
     puis retrait de l'ancienne ligne ; en cas de refus (rupture, quantité), la ligne reste telle quelle */
  const lineSwap = async (select) => {
    const row = select.closest('.cart-line');
    const scope = select.closest('#CartDrawer, #MainCart') || document;
    const prev = $('option[selected]', select);
    const id = Number(select.value);
    const qty = Math.max(1, Number(select.dataset.qty) || 1);
    let properties = {};
    try { properties = JSON.parse(select.dataset.props || '{}') || {}; } catch (e) { properties = {}; }
    if (Array.isArray(properties)) properties = {};
    if (row) row.classList.add('is-loading');
    try {
      await request(`${R.cartAdd}.js`, { items: [{ id, quantity: qty, properties }] });
    } catch (err) {
      toast(err.message || S.error, 'error');
      if (prev) select.value = prev.value;
      if (row) row.classList.remove('is-loading');
      return;
    }
    try {
      const data = await request(`${R.cartChange}.js`, { id: select.dataset.key, quantity: 0, sections: cartSections(), sections_url: window.location.pathname });
      renderCartSections(data.sections);
      updateCartCount(data.item_count);
      document.dispatchEvent(new CustomEvent('cart:changed', { detail: data }));
      toast(S.flavorChanged || S.added, 'success');
    } catch (err) {
      toast(err.message || S.error, 'error');
      await refreshCart().catch(() => {});
    }
    /* La ligne peut changer de place (article ajouté en tête du panier) : le focus revient sur sa liste de saveurs */
    const again = $$('[data-cart-swap]', scope).find((s) => s.value === String(id));
    if (again && (!document.activeElement || document.activeElement === document.body)) again.focus({ preventScroll: true });
  };

  const lineQtyChange = debounce(async (input) => {
    const line = parseInt(input.dataset.line, 10);
    const qty = Math.max(0, parseInt(input.value, 10) || 0);
    const row = input.closest('.cart-line');
    if (row) row.classList.add('is-loading');
    try { await Cart.change(line, qty); }
    catch (err) { toast(err.message || S.error, 'error'); await refreshCart().catch(() => {}); }
  }, 400);

  document.addEventListener('change', (e) => {
    const upsell = e.target.closest('[data-upsell-variant]');
    if (upsell) {
      const row = upsell.closest('.upsell__item');
      const btn = row && $('[data-quick-add]', row);
      if (btn) btn.dataset.variantId = upsell.value;
    }
    const swap = e.target.closest('[data-cart-swap]');
    if (swap) { lineSwap(swap); return; }
    const input = e.target.closest('.cart-line .qty__input');
    if (input) lineQtyChange(input);
    const note = e.target.closest('[data-cart-note]');
    if (note) request(`${R.cartUpdate}.js`, { note: note.value }).catch(() => {});
  });

  document.addEventListener('click', async (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      const row = remove.closest('.cart-line');
      if (row) row.classList.add('is-loading');
      try { await Cart.change(parseInt(remove.dataset.cartRemove, 10), 0); }
      catch (err) { toast(err.message || S.error, 'error'); if (row) row.classList.remove('is-loading'); }
      return;
    }
    const clear = e.target.closest('[data-cart-clear]');
    if (clear) {
      e.preventDefault();
      // Action destructrice : premier appui = demande de confirmation (5 s), second appui = panier vidé
      const label = $('[data-cart-clear-label]', clear) || clear;
      if (!clear.classList.contains('is-armed')) {
        clear.dataset.label = label.textContent;
        label.textContent = S.cartClearConfirm || label.textContent;
        clear.classList.add('is-armed');
        clearTimeout(clear._disarm);
        clear._disarm = setTimeout(() => { clear.classList.remove('is-armed'); label.textContent = clear.dataset.label; }, 5000);
        return;
      }
      clearTimeout(clear._disarm);
      clear.disabled = true;
      try { await Cart.clear(); } catch (err) { toast(err.message || S.error, 'error'); clear.disabled = false; }
      return;
    }
    const removeCode = e.target.closest('[data-discount-remove]');
    if (removeCode) {
      e.preventDefault();
      try {
        const cart = await getCart();
        const codes = (cart.discount_codes || []).map((d) => d.code).filter((c) => c.toLowerCase() !== removeCode.dataset.discountRemove.toLowerCase());
        await Cart.setDiscounts(codes);
        await refreshCart();
        toast(S.discountRemoved, 'info');
      } catch (err) { toast(err.message || S.error, 'error'); }
      return;
    }
    const quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      if (quick.classList.contains('is-loading')) return;
      quick.classList.add('is-loading');
      try { await Cart.add([{ id: Number(quick.dataset.variantId), quantity: 1 }], quick); }
      catch (err) { toast(err.status === 422 ? (err.message || S.soldOut) : (err.message || S.error), 'error'); }
      finally { quick.classList.remove('is-loading'); }
    }
  });

  const applyDiscountCode = async (code, btn) => {
    if (btn) btn.classList.add('is-loading');
    try {
      const cart = await getCart();
      const existing = (cart.discount_codes || []).map((d) => d.code);
      if (!existing.map((c) => c.toLowerCase()).includes(code.toLowerCase())) existing.push(code);
      const updated = await Cart.setDiscounts(existing);
      const applied = (updated.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (applied && applied.applicable) {
        toast(S.discountApplied, 'success');
      } else {
        await Cart.setDiscounts(existing.filter((c) => c.toLowerCase() !== code.toLowerCase()));
        toast(S.discountInvalid, 'error');
      }
      await refreshCart();
      return !!(applied && applied.applicable);
    } catch (err) {
      toast(err.message || S.error, 'error');
      return false;
    } finally {
      if (btn) btn.classList.remove('is-loading');
    }
  };
  PL.applyDiscountCode = applyDiscountCode;

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-cart-discount]');
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[name="discount"]');
    const code = (input.value || '').trim();
    if (!code) { input.focus(); return; }
    await applyDiscountCode(code, form.querySelector('button[type="submit"]'));
  });

  /* ---------- Sélecteur de quantité ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;
        e.preventDefault();
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const step = btn.name === 'plus' ? 1 : -1;
        const next = Math.min(max, Math.max(min, (parseInt(this.input.value, 10) || 0) + step));
        if (next === parseInt(this.input.value, 10)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Formulaire produit ----------
     Ajouter au panier (tiroir ou notification), « Acheter maintenant » (ajout puis paiement), erreur affichée sous le bouton.
     Variante épuisée : le bouton passe en « M'alerter » (géré par product.js, aucun ajout). */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      const btn = $('[data-add-button]', this.form);
      const submitter = e.submitter;
      const buyNow = !!(submitter && submitter.hasAttribute('data-buy-now'));
      const busy = buyNow ? submitter : btn;
      if (!btn || !busy || busy.disabled || busy.classList.contains('is-loading')) return;
      if (btn.dataset.mode === 'notify' || btn.dataset.mode === 'soldout' || btn.dataset.mode === 'blocked') return;
      const section = this.closest('product-info');
      const sticky = section ? $('[data-sticky-add]', section) : null;
      busy.classList.add('is-loading');
      if (sticky && !buyNow) sticky.classList.add('is-loading');
      const qtyInput = (this.form.id && document.querySelector(`input[name="quantity"][form="${this.form.id}"]`)) || $('input[name="quantity"]', this.form) || (section ? $('.product__qty .qty__input', section) : null);
      const id = Number($('[data-variant-id]', this.form).value);
      const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      const errEl = $('[data-form-error]', this.form);
      if (errEl) errEl.hidden = true;
      try {
        if (buyNow) {
          await request(`${R.cartAdd}.js`, { items: [{ id, quantity }] });
          window.location.href = submitter.value || '/checkout';
          return;
        }
        await Cart.add([{ id, quantity }], btn);
      } catch (err) {
        const msg = err.status === 422 ? (err.message || S.soldOut) : (err.message || S.error);
        toast(msg, 'error');
        if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
      } finally {
        busy.classList.remove('is-loading');
        if (sticky) sticky.classList.remove('is-loading');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Carrousel produits ---------- */
  class ProductSlider extends HTMLElement {
    connectedCallback() {
      this.track = $('.slider__track', this);
      if (!this.track) return;
      this.prev = $('[data-slider-prev]', this);
      this.next = $('[data-slider-next]', this);
      this.dotsWrap = $('.slider__dots', this);
      const step = (dir) => this.track.scrollBy({ left: dir * this.track.clientWidth * 0.9, behavior: reduceMotion ? 'auto' : 'smooth' });
      if (this.prev) this.prev.addEventListener('click', () => step(-1));
      if (this.next) this.next.addEventListener('click', () => step(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 50), { passive: true });
      window.addEventListener('resize', debounce(() => this.buildDots(), 200));
      /* Cartes ajoutées après coup (vus récemment, recommandations) : flèches recalculées */
      if ('MutationObserver' in window) new MutationObserver(debounce(() => this.buildDots(), 60)).observe(this.track, { childList: true });
      this.buildDots();
    }
    buildDots() {
      if (!this.dotsWrap) { this.update(); return; }
      const pages = Math.max(1, Math.ceil(this.track.scrollWidth / this.track.clientWidth - 0.05));
      this.dotsWrap.innerHTML = pages > 1 ? Array.from({ length: pages }, () => '<span></span>').join('') : '';
      this.update();
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      if (this.dotsWrap) {
        const dots = $$('span', this.dotsWrap);
        const idx = Math.round(this.track.scrollLeft / this.track.clientWidth);
        dots.forEach((d, i) => d.classList.toggle('is-active', i === Math.min(idx, dots.length - 1)));
      }
    }
  }
  customElements.define('product-slider', ProductSlider);

  /* ---------- Barre d'annonce (rotation mobile) ---------- */
  class AnnouncementRotator extends HTMLElement {
    connectedCallback() {
      this.slides = $$('.announcement__slide', this);
      if (this.slides.length < 2 || reduceMotion) return;
      let i = 0;
      const speed = (parseInt(this.dataset.speed, 10) || 4) * 1000;
      const tick = () => {
        this.slides[i].classList.remove('is-active'); this.slides[i].setAttribute('aria-hidden', 'true');
        $$('a', this.slides[i]).forEach((a) => a.setAttribute('tabindex', '-1'));
        i = (i + 1) % this.slides.length;
        this.slides[i].classList.add('is-active'); this.slides[i].removeAttribute('aria-hidden');
        $$('a', this.slides[i]).forEach((a) => a.removeAttribute('tabindex'));
      };
      // Bouton pause (WCAG 2.2.2) : indispensable au toucher, où le survol n'existe pas
      let paused = false;
      let timer = setInterval(tick, speed);
      const stop = () => clearInterval(timer);
      const start = () => { clearInterval(timer); if (!paused) timer = setInterval(tick, speed); };
      const btn = $('[data-ann-pause]', this);
      if (btn) {
        btn.hidden = false;
        btn.addEventListener('click', () => { paused = !paused; btn.setAttribute('aria-pressed', String(paused)); if (paused) stop(); else start(); });
      }
      this.addEventListener('mouseenter', stop);
      this.addEventListener('focusin', stop);
      this.addEventListener('touchstart', stop, { passive: true });
      this.addEventListener('mouseleave', start);
    }
  }
  customElements.define('announcement-rotator', AnnouncementRotator);

  // Barre d'annonce fixe : sa hauteur réelle (--ann-h) place l'en-tête fixe et les éléments collants juste dessous
  const annSticky = document.querySelector('[data-ann-sticky]');
  if (annSticky && 'ResizeObserver' in window) {
    new ResizeObserver(() => document.documentElement.style.setProperty('--ann-h', `${annSticky.offsetHeight}px`)).observe(annSticky);
  }

  /* ---------- En-tête collant ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky === 'true') {
        const onScroll = () => this.classList.toggle('is-scrolled', window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
      this.initMega();
      const toggle = $('[data-search-toggle]', this);
      const mobileSearch = $('#MobileSearch', this);
      if (toggle && mobileSearch) {
        toggle.addEventListener('click', () => {
          const open = mobileSearch.hidden;
          mobileSearch.hidden = !open;
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (open) $('input[type="search"]', mobileSearch).focus();
        });
      }
    }
    initMega() {
      const items = $$('.header__menu-item.has-mega', this);
      if (!items.length) return;
      let openTimer;
      let closeTimer;
      const close = (item, restoreFocus) => {
        item.classList.remove('is-open');
        const btn = $('.header__mega-toggle', item);
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (restoreFocus && btn) btn.focus();
      };
      const closeAll = (except) => items.forEach((it) => { if (it !== except) close(it); });
      const open = (item) => {
        closeAll(item);
        item.classList.add('is-open');
        const btn = $('.header__mega-toggle', item);
        if (btn) btn.setAttribute('aria-expanded', 'true');
      };
      items.forEach((item) => {
        // Survol avec temporisation (évite les ouvertures involontaires)
        item.addEventListener('mouseenter', () => {
          if (!window.matchMedia('(hover: hover)').matches) return;
          clearTimeout(closeTimer);
          openTimer = setTimeout(() => open(item), 120);
        });
        item.addEventListener('mouseleave', () => {
          clearTimeout(openTimer);
          closeTimer = setTimeout(() => close(item), 180);
        });
        const btn = $('.header__mega-toggle', item);
        btn.addEventListener('click', () => {
          if (item.classList.contains('is-open')) close(item);
          else { open(item); const first = $('.mega a', item); if (first) first.focus(); }
        });
        item.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && item.classList.contains('is-open')) { e.stopPropagation(); close(item, true); }
          if (e.key === 'ArrowDown' && e.target.matches('.header__menu-link, .header__mega-toggle')) {
            e.preventDefault(); open(item); const first = $('.mega a', item); if (first) first.focus();
          }
        });
        item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(item); });
      });
      document.addEventListener('click', (e) => { if (!e.target.closest('.header__menu-item.has-mega')) closeAll(); });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Recherche prédictive ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('.search-form__results', this);
      this.status = $('[data-predictive-status]', this);
      this.reset = $('.search-form__reset', this);
      if (!this.input) return;
      this.input.addEventListener('input', () => {
        if (this.reset) this.reset.hidden = !this.input.value;
      });
      if (this.reset) this.reset.addEventListener('click', () => { setTimeout(() => { this.reset.hidden = true; this.close(); this.input.focus(); }); });
      if (this.dataset.enabled !== 'true' || !R.predictive) return;
      this.cache = {};
      this.input.addEventListener('input', debounce(() => this.search(), 250));
      this.input.addEventListener('focus', () => { if (this.input.value.trim().length > 1 && this.results.innerHTML) this.open(); });
      this.addEventListener('keydown', (e) => this.onKey(e));
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
    }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.close(); return; }
      if (this.cache[q]) { this.render(this.cache[q]); return; }
      const types = ['product', 'collection', 'query'];
      if (PL.settings.predictiveArticles) types.push('article', 'page');
      const url = `${R.predictive}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[type]=${types.join(',')}&resources[limit]=6&resources[options][unavailable_products]=last`;
      try {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error();
        const html = $('#predictive-search-results', parseHTML(await res.text()));
        const markup = html ? html.outerHTML : '';
        this.cache[q] = markup;
        this.render(markup);
      } catch (err) {
        if (err.name !== 'AbortError') this.close();
      }
    }
    render(markup) {
      this.results.innerHTML = markup;
      if (!markup) { this.close(); return; }
      this.open();
      const count = $$('.predictive__product', this.results).length;
      if (this.status) this.status.textContent = count ? `${count}` : (S.noResults || '');
    }
    open() { this.results.hidden = false; }
    close() { this.results.hidden = true; }
    onKey(e) {
      if (e.key === 'Escape') { this.close(); this.input.focus(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const links = $$('a', this.results);
      if (!links.length || this.results.hidden) return;
      e.preventDefault();
      const i = links.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? Math.min(links.length - 1, i + 1) : i - 1;
      if (next < 0) this.input.focus(); else links[next].focus();
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Favoris (localStorage) ---------- */
  const WL_KEY = 'pl_wishlist';
  const Wishlist = {
    list() { const l = storage.get(WL_KEY, []); return Array.isArray(l) ? l : []; },
    has(h) { return this.list().includes(h); },
    toggle(h) {
      const l = this.list();
      const i = l.indexOf(h);
      if (i > -1) l.splice(i, 1); else l.unshift(h);
      if (!storage.set(WL_KEY, l)) { toast(S.error, 'error'); return this.has(h); }
      this.sync();
      return i === -1;
    },
    remove(h) { storage.set(WL_KEY, this.list().filter((x) => x !== h)); this.sync(); },
    sync() {
      const count = this.list().length;
      $$('[data-wishlist-count]').forEach((el) => { el.textContent = count; el.hidden = count === 0; });
      $$('[data-wishlist-count-text]').forEach((el) => { el.textContent = count; });
      initWishlistButtons(document);
      WishlistSync.queue();
    }
  };
  PL.wishlist = Wishlist;

  /* Favoris liés au compte client : lus dans le métachamp custom.favoris (rendu par Liquid), fusionnés avec ceux de l'appareil,
     puis enregistrés par le service de synchronisation s'il est configuré (identité signée HMAC). Fusion à trois voies :
     un favori retiré sur un autre appareil n'est pas réintroduit, un favori ajouté ici hors connexion est conservé. */
  const WL_BASE = 'pl_wishlist_base';
  const WishlistSync = {
    ready: false,
    init() {
      const account = PL.settings.customerWishlist;
      if (!Array.isArray(account) || !PL.settings.wishlist) return;
      const remote = account.filter((h) => typeof h === 'string' && h);
      const local = Wishlist.list();
      const base = storage.get(WL_BASE, null);
      let merged;
      if (Array.isArray(base)) {
        const removedHere = base.filter((h) => !local.includes(h));
        const addedHere = local.filter((h) => !base.includes(h));
        merged = [...addedHere, ...remote.filter((h) => !removedHere.includes(h))];
      } else {
        merged = [...local, ...remote];
      }
      merged = [...new Set(merged)].slice(0, 100);
      storage.set(WL_KEY, merged);
      storage.set(WL_BASE, remote);
      this.last = JSON.stringify(remote);
      this.ready = true;
      Wishlist.sync();
    },
    queue() {
      if (!this.ready || !PL.settings.wishlistSync) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.push(), 800);
    },
    async push() {
      const cfg = PL.settings.wishlistSync;
      const list = Wishlist.list();
      const body = JSON.stringify(list);
      if (body === this.last) return;
      try {
        const res = await fetch(cfg.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customer: { id: cfg.id, email: cfg.email, ts: cfg.ts, sig: cfg.sig }, wishlist: list })
        });
        if (!res.ok) throw new Error(String(res.status));
        this.last = body;
        storage.set(WL_BASE, list);
        if (!this.notified) { this.notified = true; toast(S.wishlistSynced, 'info'); }
      } catch (err) {
        if (!this.failed) { this.failed = true; toast(S.wishlistSyncError, 'error'); }
      }
    }
  };
  PL.wishlistSync = WishlistSync;

  function initWishlistButtons(ctx) {
    if (PL.compare) PL.compare.syncButtons(ctx);
    if (!PL.settings.wishlist) return;
    $$('[data-wishlist-toggle]', ctx).forEach((btn) => {
      const on = Wishlist.has(btn.dataset.handle);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      /* Texte visible : il reste le nom accessible (WCAG 2.5.3), l'état est donné par aria-pressed */
      if ($('.wishlist-btn__label', btn)) return;
      const label = btn.getAttribute('aria-label') || '';
      const name = label.includes(':') ? label.split(':').slice(1).join(':').trim() : '';
      btn.setAttribute('aria-label', `${on ? S.wishlistRemove : S.wishlistAdd}${name ? ' : ' + name : ''}`);
    });
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const added = Wishlist.toggle(btn.dataset.handle);
    btn.classList.remove('is-pop'); void btn.offsetWidth; btn.classList.add('is-pop');
    toast(added ? S.wishlistAdded : S.wishlistRemoved, added ? 'success' : 'info');
    const page = btn.closest('[data-wishlist-page]');
    if (page && !added) {
      const li = btn.closest('li');
      if (li) li.remove();
      if (PL.wishlistPage) PL.wishlistPage.toggleEmpty();
    }
  });

  const productBase = () => `${(R.root || '/').replace(/\/$/, '')}/products/`;
  /* ---------- Outils données produit ---------- */
  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = (v) => String(v ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const num = (v) => {
    if (typeof v === 'number') return v;
    const m = String(v ?? '').replace(/\s| | /g, '').replace(',', '.').match(/\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : NaN;
  };
  const fill = (str, map) => String(str || '').replace(/\[(\w+)\]/g, (_, k) => (map[k] !== undefined ? map[k] : ''));
  const fetchProductData = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw Object.assign(new Error('http'), { status: res.status });
    const el = $('[data-product-data]', parseHTML(await res.text()));
    return el ? JSON.parse(el.textContent.trim() || 'null') : null;
  };
  const productDataUrl = (h) => `${productBase()}${encodeURIComponent(h)}?section_id=product-data`;
  const starsHTML = (r) => {
    const pct = Math.max(0, Math.min(100, (r / 5) * 100));
    const star = '<svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3.2l2.7 5.5 6 .9-4.35 4.2 1 6L12 17l-5.35 2.8 1-6L3.3 9.6l6-.9z"/></svg>';
    return `<span class="rating__stars" style="--pct:${pct}%" aria-hidden="true"><span class="rating__bg">${star.repeat(5)}</span><span class="rating__fg">${star.repeat(5)}</span></span>`;
  };
  const buyButton = (p, cls = 'btn btn--primary btn--small') => (p.single && p.av
    ? `<button type="button" class="${cls}" data-quick-add data-variant-id="${p.vid}">${esc(S.addToCart)}</button>`
    : `<a href="${esc(p.u)}" class="${cls}">${esc(p.av ? S.chooseOptions : (p.blk ? S.unavailable : S.soldOut))}</a>`);

  /* ---------- Comparateur (liste) ---------- */
  const CMP_KEY = 'pl_compare';
  const Compare = {
    max() { return PL.settings.compareMax || 3; },
    list() { const l = storage.get(CMP_KEY, []); return Array.isArray(l) ? l.filter((x) => x && x.h) : []; },
    has(h) { return this.list().some((x) => x.h === h); },
    save(l) { storage.set(CMP_KEY, l.slice(0, this.max())); this.sync(); document.dispatchEvent(new CustomEvent('compare:updated')); },
    toggle(item) {
      const l = this.list();
      if (this.has(item.h)) { this.save(l.filter((x) => x.h !== item.h)); return false; }
      if (l.length >= this.max()) { toast(S.compareFull, 'error'); return null; }
      this.save([...l, item]);
      return true;
    },
    set(items) { this.save(items); },
    remove(h) { this.save(this.list().filter((x) => x.h !== h)); },
    clear() { this.save([]); },
    syncButtons(ctx = document) {
      $$('[data-compare-toggle]', ctx).forEach((b) => {
        const on = this.has(b.dataset.handle);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if ($('.compare-btn__label', b)) return;
        const name = b.dataset.title ? ` : ${b.dataset.title}` : '';
        b.setAttribute('aria-label', `${on ? S.compareRemove : S.compareAdd}${name}`);
      });
    },
    sync() {
      if (!PL.settings.compare) return;
      this.syncButtons();
      const bar = $('[data-compare-bar]');
      if (!bar) return;
      const l = this.list();
      bar.hidden = l.length === 0 || !!$('compare-table');
      document.body.classList.toggle('has-compare-bar', !bar.hidden);
      $('[data-compare-count]', bar).textContent = l.length;
      $('[data-compare-items]', bar).innerHTML = l.map((x) => `<li><span class="compare-bar__thumb">${x.img ? `<img src="${esc(x.img)}" alt="" width="44" height="44" loading="lazy">` : ''}</span><span class="compare-bar__name">${esc(x.t)}</span><button type="button" class="compare-bar__remove" data-compare-remove="${esc(x.h)}" aria-label="${esc(S.compareRemove)} : ${esc(x.t)}"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></li>`).join('');
      const go = $('[data-compare-go]', bar);
      if (go) go.classList.toggle('is-disabled', l.length < 2);
    }
  };
  PL.compare = Compare;

  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-compare-toggle]');
    if (t) {
      e.preventDefault(); e.stopPropagation();
      const r = Compare.toggle({ h: t.dataset.handle, t: t.dataset.title || t.dataset.handle, img: t.dataset.img || '' });
      const bar = $('[data-compare-bar]');
      if (r === true) toast(S.compareAdded, 'success', bar && Compare.list().length > 1 ? { label: S.compareGo, href: bar.dataset.url } : null);
      else if (r === false) toast(S.compareRemoved, 'info');
      return;
    }
    const rm = e.target.closest('[data-compare-remove]');
    if (rm) { e.preventDefault(); Compare.remove(rm.dataset.compareRemove); return; }
    const cl = e.target.closest('[data-compare-clear]');
    if (cl) { e.preventDefault(); Compare.clear(); }
  });

  /* ---------- Badges intelligents : compléments calculés dans le navigateur ----------
     Les badges sont rendus par snippets/badges.liquid (tous, ceux au-delà de la limite masqués).
     Ici : badge « compatible » (produits consultés, sans rien envoyer) et, sur la fiche, mise à jour au changement de variante. */
  const BADGE_ORDER = ['sold_out', 'compatible', 'low_stock', 'sale', 'back_in_stock', 'new', 'bestseller', 'top_rated', 'pack', 'web_exclusive', 'clearance', 'nicotine_free', 'puffs', 'rechargeable', 'nic_salts', 'made_in_france', 'flavor_family', 'loyalty'];
  const BADGE_FAMILY = { sale: 'offer', bestseller: 'offer', pack: 'offer', clearance: 'offer', loyalty: 'offer', sold_out: 'stock', low_stock: 'stock', back_in_stock: 'stock' };
  const Badges = {
    tpl(type) {
      const t = document.getElementById('PLBadgeTemplates');
      return t ? t.content.querySelector(`[data-badge="${type}"]`) : null;
    },
    make(type, value) {
      const base = this.tpl(type);
      if (!base) return null;
      const el = base.cloneNode(true);
      if (type === 'low_stock') {
        const label = value === 1 ? base.dataset.one : base.dataset.other;
        $('.badge__text', el).textContent = String(label || '').replace('7777', value);
      } else if (value !== undefined && value !== '') {
        $$('.badge__text, .visually-hidden', el).forEach((n) => { n.textContent = n.textContent.replace('7777', value); });
      }
      return el;
    },
    /* Un emplacement n'accepte que ses familles (data-only) et refuse ses types exclus (data-exclude) ; jamais de badge dans une ligne d'état */
    allowed(box, type) {
      if (box.hasAttribute('data-line')) return false;
      const only = box.dataset.only;
      if (only && !only.split(',').includes(BADGE_FAMILY[type] || 'info')) return false;
      return !(box.dataset.exclude || '').includes(`,${type},`);
    },
    set(box, type, value) {
      if (!this.allowed(box, type)) return;
      const old = $(`[data-badge="${type}"]`, box);
      if (value === null || value === undefined || value === false) {
        if (old) old.remove();
      } else {
        const el = this.make(type, value);
        if (el) { if (old) old.replaceWith(el); else box.appendChild(el); }
      }
      this.apply(box);
    },
    apply(box) {
      const n = parseInt(box.dataset.limit, 10);
      const max = Number.isNaN(n) ? 2 : n;
      const items = $$('[data-badge]', box);
      items.sort((a, b) => (Number(a.dataset.priority) - Number(b.dataset.priority)) || (BADGE_ORDER.indexOf(a.dataset.badge) - BADGE_ORDER.indexOf(b.dataset.badge)));
      let shown = 0;
      items.forEach((el) => {
        box.appendChild(el);
        if (el.hasAttribute('data-variant-off')) { el.hidden = true; return; }
        el.hidden = shown >= max;
        shown += 1;
      });
      box.toggleAttribute('data-empty', !shown);
    },
    /* Fiche produit : rupture, stock faible et promo de la variante choisie ; badges propres à certaines variantes
       (sans nicotine, famille de goût : data-variants) */
    variant(box, v) {
      if (!box || !v || box.hasAttribute('data-line')) return;
      $$('[data-variants]', box).forEach((el) => el.toggleAttribute('data-variant-off', !el.dataset.variants.includes(`;${v.id};`)));
      this.set(box, 'sold_out', v.available ? null : '');
      this.set(box, 'low_stock', v.available && v.low > 0 ? v.low : null);
      const sale = this.tpl('sale');
      const pct = v.compare_at_price > v.price ? Math.floor(((v.compare_at_price - v.price) * 100) / v.compare_at_price) : 0;
      this.set(box, 'sale', sale && pct >= Number(sale.dataset.min || 1) ? pct : null);
      this.apply(box);
    },
    /* « Compatible avec votre appareil » : le produit figure dans les compatibles d'un produit consulté, ou l'inverse */
    compat(ctx = document) {
      if (!this.tpl('compatible')) return;
      const viewed = (storage.get('pl_recent', []) || []).filter((h) => typeof h === 'string');
      if (!viewed.length) return;
      const map = storage.get('pl_compat', {}) || {};
      const fits = new Set();
      viewed.forEach((h) => (Array.isArray(map[h]) ? map[h] : []).forEach((x) => fits.add(x)));
      const here = document.querySelector('product-info[data-handle]');
      const current = here ? here.dataset.handle : '';
      $$('[data-badges][data-handle]', ctx).forEach((box) => {
        const h = box.dataset.handle;
        if (!h || h === current || box.closest('[data-no-compat-badge]') || $('[data-badge="compatible"]', box)) return;
        const list = (box.dataset.compat || '').split(',').filter(Boolean);
        if (fits.has(h) || list.some((x) => x !== current && viewed.includes(x))) this.set(box, 'compatible', '');
      });
    }
  };
  PL.badges = Badges;
  if ('MutationObserver' in window) {
    const badgeWatch = debounce(() => Badges.compat(document), 150);
    new MutationObserver((records) => {
      if (records.some((r) => Array.from(r.addedNodes).some((n) => n.nodeType === 1 && (n.matches('[data-badges]') || n.querySelector('[data-badges]'))))) badgeWatch();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ---------- Programme de fidélité ---------- */
  const Loyalty = {
    data: null,
    init() {
      const el = document.getElementById('LoyaltyData');
      if (!el) return;
      try { this.data = JSON.parse(el.textContent); } catch (e) { this.data = null; }
      if (!this.data || !this.data.enabled) { this.data = null; return; }
      this.data.tiers.sort((a, b) => a.points - b.points);
      document.addEventListener('variant:change', (e) => {
        const box = e.target.closest('product-info') && $('[data-loyalty="product"]', e.target.closest('product-info'));
        if (box && e.detail) { box.dataset.price = e.detail.price; box.dataset.compare = e.detail.compare_at_price || 0; this.renderProduct(box); }
      });
      document.addEventListener('change', (e) => {
        const info = e.target.closest('product-info');
        if (info && e.target.matches('.product__qty .qty__input')) { const box = $('[data-loyalty="product"]', info); if (box) this.renderProduct(box); }
      });
      document.addEventListener('click', async (e) => {
        const apply = e.target.closest('[data-loyalty-apply]');
        if (apply) { e.preventDefault(); await applyDiscountCode(apply.dataset.loyaltyApply, apply); return; }
        const copy = e.target.closest('[data-loyalty-copy]');
        if (copy) {
          e.preventDefault();
          try { await navigator.clipboard.writeText(copy.dataset.loyaltyCopy); toast(this.s('copied'), 'success'); } catch (err) { window.prompt('', copy.dataset.loyaltyCopy); }
        }
      });
      this.render();
    },
    s(key, map = {}) { return fill(this.data.strings[key] || '', map); },
    // Unité accordée au nombre (« 1 couronne », « 50 couronnes ») : singulier pour 0 et 1, comme snippets/loyalty-unit
    pts(n) {
      const v = Math.max(0, Math.floor(n));
      const u = String(this.data.unit || '');
      return `${v.toLocaleString(document.documentElement.lang || 'fr')} ${v < 2 && /s$/.test(u) ? u.slice(0, -1) : u}`;
    },
    earn(cents) { return Math.floor(Math.max(0, cents) / 100) * this.data.rate; },
    // Articles en promotion (prix barré supérieur au prix) : couronnes multipliées (×2), surplus crédité par Shopify Flow
    factor() { return Math.max(1, Number(this.data.promoFactor) || 1); },
    x2Chip() { return `<span class="loyalty-x2">${this.icon('crown')}${esc(this.s('promoX2'))}</span>`; },
    tierAt(points) {
      const t = this.data.tiers;
      let i = -1;
      t.forEach((tier, k) => { if (points >= tier.points) i = k; });
      return { current: t[i] || null, next: t[i + 1] || null, index: i };
    },
    progressHTML(points, extra = 0) {
      const { current, next } = this.tierAt(points);
      if (!next) return `<p class="loyalty-box__line loyalty-box__line--max">${this.icon('crown')}${esc(this.s('maxTier'))}</p>`;
      const base = current ? current.points : 0;
      const pct = Math.min(100, Math.round(((points - base) / Math.max(1, next.points - base)) * 100));
      const pctAfter = Math.min(100, Math.round(((points + extra - base) / Math.max(1, next.points - base)) * 100));
      const unlock = extra > 0 && points + extra >= next.points;
      const text = unlock ? this.s('unlock', { tier: next.name }) : this.s('toNext', { points: this.pts(next.points - points - extra), tier: next.name, perk: next.perk });
      return `<div class="loyalty-progress" role="img" aria-label="${esc(text)}">
          <div class="loyalty-progress__bar"><span class="loyalty-progress__after" style="width:${pctAfter}%"></span><span class="loyalty-progress__now" style="width:${pct}%"></span></div>
          <div class="loyalty-progress__labels"><span>${esc(current ? current.name : '')}</span><span>${esc(next.name)} · ${esc(this.pts(next.points))}</span></div>
        </div>
        <p class="loyalty-box__line${unlock ? ' loyalty-box__line--unlock' : ''}">${this.icon(unlock ? 'sparkle' : 'arrow')}${esc(text)}</p>`;
    },
    icon(name) {
      const paths = {
        gift: '<rect x="3.5" y="8.5" width="17" height="4" rx="1"/><path d="M5 12.5V20h14v-7.5M12 8.5V20M12 8.5C10.5 5 7 4.5 7 6.5s5 2 5 2zM12 8.5c1.5-3.5 5-4 5-2s-5 2-5 2z"/>',
        crown: '<path d="M 1 10 L 6.06 12.21 A 2.12 2.12 0 1 0 10.21 11.11 L 9.28 10.28 Q 11.13 9.63 11.95 5.4 Q 12.78 9.63 14.81 10.28 L 13.89 11.11 A 2.12 2.12 0 1 0 18.03 12.21 L 23 9.63 L 18.86 17.09 Q 12.05 14.51 5.05 17.18Z"/><path d="M 12.05 12.21 L 13.06 13.5 L 12.05 14.79 L 11.03 13.5Z"/><path d="M 5.23 18.56 Q 12.05 16.08 18.86 18.29"/>',
        sparkle: '<path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/>',
        arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>',
        star: '<path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.8z"/>'
      };
      return `<svg class="icon icon-${name}" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[name] || ''}</svg>`;
    },
    tierChip(tier) { return tier ? `<span class="loyalty-chip">${this.icon('crown')}${esc(this.s('tier', { tier: tier.name }))}</span>` : ''; },
    guestButtons() {
      const d = this.data;
      return `<div class="loyalty-box__actions"><a href="${esc(d.registerUrl)}" class="btn btn--primary btn--small">${esc(this.s('join'))}</a><a href="${esc(d.loginUrl)}" class="btn btn--outline btn--small">${esc(this.s('login'))}</a></div>`;
    },
    renderCart(box) {
      const d = this.data;
      const total = parseInt(box.dataset.total, 10) || 0;
      const promo = Math.min(total, parseInt(box.dataset.promo, 10) || 0);
      const bonus = this.earn(promo) * (this.factor() - 1);
      const earned = this.earn(total) + bonus;
      const codes = (box.dataset.codes || '').toLowerCase().split(',').filter(Boolean);
      let html = `<div class="loyalty-box__head">${this.icon('crown')}<strong>${esc(d.name)}</strong>${d.loggedIn ? this.tierChip(this.tierAt(d.points).current) : ''}</div>`;
      if (d.loggedIn) {
        html += `<p class="loyalty-box__earn">${this.s('earnCart', { points: `<strong>${esc(this.pts(earned))}</strong>` })}</p>`;
        if (bonus > 0) html += `<p class="loyalty-box__line loyalty-box__line--x2">${this.x2Chip()}${esc(this.s('promoX2Cart', { points: this.pts(bonus) }))}</p>`;
        html += `<p class="loyalty-box__line">${esc(this.s('balance', { points: this.pts(d.points) }))}</p>`;
        html += this.progressHTML(d.points, earned);
        const { current } = this.tierAt(d.points);
        if (current && current.code && current.percent > 0) {
          html += codes.includes(current.code.toLowerCase())
            ? `<p class="loyalty-box__applied">${this.icon('star')}${esc(this.s('discountActive', { tier: current.name }))}</p>`
            : `<button type="button" class="btn btn--secondary btn--small btn--block" data-loyalty-apply="${esc(current.code)}">${esc(this.s('useDiscount', { tier: current.name, percent: current.percent }))}</button>`;
        }
      } else {
        html += `<p class="loyalty-box__earn">${this.s('guestCart', { points: `<strong>${esc(this.pts(earned))}</strong>` })}</p>`;
        if (bonus > 0) html += `<p class="loyalty-box__line loyalty-box__line--x2">${this.x2Chip()}${esc(this.s('promoX2Cart', { points: this.pts(bonus) }))}</p>`;
        if (d.welcome) html += `<p class="loyalty-box__line">${esc(this.s('welcomeOffer', { points: this.pts(d.welcome) }))}</p>`;
        html += this.guestButtons();
      }
      box.innerHTML = html;
      box.hidden = false;
    },
    renderProduct(box) {
      const d = this.data;
      const info = box.closest('product-info');
      const qtyInput = info && $('.product__qty .qty__input', info);
      const qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      const price = parseInt(box.dataset.price, 10) || 0;
      const sale = (parseInt(box.dataset.compare, 10) || 0) > price;
      const earned = this.earn(price * qty) * (sale ? this.factor() : 1);
      box.classList.toggle('loyalty-line--x2', sale);
      let html = `<span class="loyalty-line__icon">${this.icon('crown')}</span><span class="loyalty-line__text">`;
      html += d.loggedIn ? this.s('earnProduct', { points: `<strong>${esc(this.pts(earned))}</strong>` }) : this.s('guestProduct', { points: `<strong>${esc(this.pts(earned))}</strong>` });
      if (sale) html += ` ${this.x2Chip()}`;
      if (d.loggedIn) {
        const { next } = this.tierAt(d.points);
        html += `<span class="loyalty-line__sub">${esc(this.s('balance', { points: this.pts(d.points) }))}${next ? ` · ${esc(this.s('toNext', { points: this.pts(Math.max(0, next.points - d.points)), tier: next.name, perk: next.perk }))}` : ''}</span>`;
      } else if (d.welcome) {
        html += `<span class="loyalty-line__sub"><a href="${esc(d.registerUrl)}" class="link link--pink">${esc(this.s('join'))}</a> · ${esc(this.s('welcomeOffer', { points: this.pts(d.welcome) }))}</span>`;
      }
      box.innerHTML = `${html}</span>`;
      box.hidden = false;
    },
    renderStatus(box) {
      const d = this.data;
      const compact = box.dataset.variant === 'compact';
      if (!d.loggedIn) {
        box.innerHTML = `<div class="loyalty-status loyalty-status--guest">
          ${d.welcome ? `<p class="loyalty-status__offer">${this.icon('crown')}${esc(this.s('welcomeOffer', { points: this.pts(d.welcome) }))}</p>` : ''}
          ${this.guestButtons()}</div>`;
        box.hidden = false;
        return;
      }
      const { current } = this.tierAt(d.points);
      box.innerHTML = `<div class="loyalty-status">
        <div class="loyalty-status__top">
          <div><p class="loyalty-status__hello">${esc(this.s('hello', { name: d.firstName }))}</p>
          <p class="loyalty-status__points">${esc(this.pts(d.points))}</p></div>
          ${this.tierChip(current)}
        </div>
        ${this.progressHTML(d.points)}
        ${!compact && current && current.perk ? `<p class="loyalty-box__line">${this.icon('star')}${esc(this.s('yourPerk', { perk: current.perk }))}</p>` : ''}
        ${current && current.code && current.percent > 0 ? `<p class="loyalty-status__code">${esc(this.s('yourCode', { code: current.code }))} <button type="button" class="link link--pink" data-loyalty-copy="${esc(current.code)}">${esc(this.s('copy'))}</button></p>` : ''}
        ${compact ? '' : `<a href="${esc(d.url)}" class="link">${esc(this.s('discover'))}${this.icon('arrow')}</a>`}
      </div>`;
      box.hidden = false;
    },
    render(ctx = document) {
      if (!this.data) return;
      $$('[data-loyalty="cart"]', ctx).forEach((b) => this.renderCart(b));
      $$('[data-loyalty="product"]', ctx).forEach((b) => this.renderProduct(b));
      $$('[data-loyalty="status"]', ctx).forEach((b) => this.renderStatus(b));
      $$('[data-loyalty-points]', ctx).forEach((b) => { b.textContent = this.pts(this.data.points); });
      $$('[data-loyalty-tier]', ctx).forEach((b) => { const t = this.tierAt(this.data.points).current; b.textContent = t ? t.name : ''; });
      $$('[data-loyalty="header"]', ctx).forEach((b) => {
        if (!this.data.loggedIn) return;
        b.innerHTML = `${this.icon('crown')}<span>${esc(this.pts(this.data.points))}</span>`;
        b.setAttribute('aria-label', `${this.data.name} : ${this.pts(this.data.points)}`);
        b.hidden = false;
      });
    }
  };
  PL.loyalty = Loyalty;

  /* ---------- Vérification d'âge ---------- */
  class AgeGate extends HTMLElement {
    connectedCallback() {
      if (window.Shopify && window.Shopify.designMode) return;
      const until = storage.get('pl_age_ok', 0);
      if (until && until > Date.now()) return;
      this.hidden = false;
      document.body.classList.add('has-overlay');
      const yes = $('[data-age-yes]', this);
      const no = $('[data-age-no]', this);
      this.release = trapFocus(this);
      setTimeout(() => yes && yes.focus(), 50);
      const confirm = () => {
        const days = parseInt(this.dataset.days, 10) || 30;
        storage.set('pl_age_ok', Date.now() + days * 86400000);
      };
      yes.addEventListener('click', () => {
        confirm();
        this.hidden = true;
        document.body.classList.remove('has-overlay');
        this.release();
      });
      no.addEventListener('click', () => { window.location.href = this.dataset.noUrl || 'https://www.google.com'; });
      // Continuer avec Google : vaut confirmation de majorité, pour ne pas revoir la fenêtre au retour de la connexion
      const login = $('[data-age-login]', this);
      if (login) login.addEventListener('click', confirm);
    }
  }
  customElements.define('age-gate', AgeGate);

  /* ---------- Langues ---------- */
  // Choix d'une langue (bouton de l'en-tête, menu, pied de page) : retour visuel pendant le rechargement, choix mémorisé (pl_lang_pref)
  document.addEventListener('submit', (e) => {
    if (e.submitter && e.submitter.name === 'language_code') {
      e.submitter.classList.add('is-loading');
      e.target.setAttribute('aria-busy', 'true');
      storage.set('pl_lang_pref', e.submitter.value);
    }
  });

  // Suggestion de langue selon le pays (snippets/language-suggest) : version française seulement, jamais pour un robot.
  // Pays → langue ; pays à plusieurs langues (Belgique, Suisse, Luxembourg, Canada) et pays sans langue publiée : langue du navigateur.
  const LANG_BY_COUNTRY = {
    GB: 'en', IE: 'en', US: 'en', AU: 'en', NZ: 'en', MT: 'en', GI: 'en',
    DE: 'de', AT: 'de', LI: 'de', ES: 'es', AD: 'es', IT: 'it', SM: 'it', VA: 'it',
    NL: 'nl', PT: 'pt-PT', SE: 'sv', DK: 'da', CZ: 'cs'
  };
  const FRENCH_COUNTRIES = ['FR', 'MC', 'RE', 'GP', 'MQ', 'GF', 'YT', 'PM', 'NC', 'PF', 'WF', 'BL', 'MF'];
  const LANG_SUGGEST_TEXT = {
    en: ['Puff Land is also available in English.', 'View in English', 'Stay in French'],
    es: ['Puff Land también está disponible en español.', 'Ver en español', 'Seguir en francés'],
    de: ['Puff Land gibt es auch auf Deutsch.', 'Auf Deutsch ansehen', 'Auf Französisch bleiben'],
    nl: ['Puff Land is ook beschikbaar in het Nederlands.', 'Bekijk in het Nederlands', 'In het Frans blijven'],
    sv: ['Puff Land finns även på svenska.', 'Visa på svenska', 'Fortsätt på franska'],
    da: ['Puff Land findes også på dansk.', 'Se på dansk', 'Fortsæt på fransk'],
    it: ['Puff Land è disponibile anche in italiano.', 'Vedi in italiano', 'Resta in francese'],
    'pt-PT': ['A Puff Land também está disponível em português.', 'Ver em português', 'Continuar em francês'],
    cs: ['Puff Land je k dispozici i v češtině.', 'Zobrazit česky', 'Zůstat ve francouzštině']
  };
  class LangSuggest extends HTMLElement {
    async connectedCallback() {
      if (window.Shopify && window.Shopify.designMode) return;
      if (navigator.webdriver || /bot|crawl|spider|slurp|lighthouse|headless|preview|facebookexternalhit/i.test(navigator.userAgent)) return;
      let langs = [];
      try { langs = JSON.parse(this.dataset.langs || '[]'); } catch (e) { return; }
      const current = this.dataset.current || 'fr';
      const isos = langs.map((l) => l.iso).filter((iso) => iso !== current);
      const pick = (code) => {
        const c = String(code || '').toLowerCase();
        return isos.find((iso) => iso.toLowerCase() === c) || isos.find((iso) => iso.toLowerCase().split('-')[0] === c.split('-')[0]) || '';
      };
      // Langue déjà choisie lors d'une visite précédente : rouverte d'office ; « fr » : rien à proposer
      const pref = storage.get('pl_lang_pref', '');
      if (pref === current) return;
      if (pref && pick(pref)) { this.switchTo(pick(pref)); return; }
      const nav = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || '']).map((l) => String(l).toLowerCase());
      if (nav[0] && nav[0].split('-')[0] === current.split('-')[0]) return;
      let country = '';
      try {
        const res = await fetch(`${this.dataset.root || '/'}browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const data = await res.json();
          country = String((data && data.detected_values && data.detected_values.country && data.detected_values.country.handle) || '').toUpperCase();
        }
      } catch (e) { /* géolocalisation indisponible : langue du navigateur */ }
      if (FRENCH_COUNTRIES.includes(country)) return;
      let target = LANG_BY_COUNTRY[country] ? pick(LANG_BY_COUNTRY[country]) : '';
      if (!target) target = nav.map(pick).find(Boolean) || '';
      if (!target || !LANG_SUGGEST_TEXT[target]) return;
      this.show(target);
    }
    switchTo(iso) {
      const input = $('[data-lang-suggest-code]', this);
      const form = $('form', this);
      if (!input || !form) return;
      input.value = iso;
      form.submit();
    }
    show(iso) {
      const [text, go, stay] = LANG_SUGGEST_TEXT[iso];
      const flag = $(`[data-lang-suggest-flag="${iso}"]`, this);
      if (flag) flag.hidden = false;
      const p = $('[data-lang-suggest-text]', this);
      p.textContent = text;
      p.lang = iso;
      const goBtn = $('[data-lang-suggest-go]', this);
      const stayBtn = $('[data-lang-suggest-stay]', this);
      goBtn.textContent = go;
      goBtn.lang = iso;
      stayBtn.textContent = stay;
      stayBtn.lang = iso;
      $('[data-lang-suggest-code]', this).value = iso;
      $('form', this).addEventListener('submit', () => {
        storage.set('pl_lang_pref', iso);
        goBtn.classList.add('is-loading');
      });
      stayBtn.addEventListener('click', () => {
        storage.set('pl_lang_pref', this.dataset.current || 'fr');
        this.hidden = true;
      });
      // Après la fenêtre de contrôle d'âge, jamais par-dessus
      const reveal = () => { this.hidden = false; };
      if (!document.body.classList.contains('has-overlay')) { reveal(); return; }
      const mo = new MutationObserver(() => {
        if (!document.body.classList.contains('has-overlay')) { mo.disconnect(); reveal(); }
      });
      mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }
  }
  customElements.define('lang-suggest', LangSuggest);
  // Liste déroulante de l'en-tête : fermée au clic à côté et avec Échap
  document.addEventListener('click', (e) => {
    $$('details[data-lang-switch][open]').forEach((d) => { if (!d.contains(e.target)) d.open = false; });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    $$('details[data-lang-switch][open]').forEach((d) => { d.open = false; const s = $('summary', d); if (s) s.focus(); });
  });



  /* ---------- Aperçu rapide ---------- */
  const loadScript = (src) => new Promise((resolve, reject) => {
    if (!src || document.querySelector(`script[src="${src}"]`)) { resolve(); return; }
    const el = document.createElement('script');
    el.src = src; el.onload = resolve; el.onerror = reject;
    document.head.appendChild(el);
  });
  const QuickView = {
    async open(handle, opener) {
      if (!this.dialog) {
        this.dialog = document.createElement('dialog');
        this.dialog.className = 'qv';
        this.dialog.setAttribute('aria-labelledby', 'QuickViewTitle');
        this.dialog.innerHTML = `<button type="button" class="qv__close icon-btn" data-qv-close aria-label="${esc(S.qvClose)}"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button><div class="qv__body" data-qv-body></div>`;
        document.body.appendChild(this.dialog);
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog || e.target.closest('[data-qv-close]')) this.close(); });
        this.dialog.addEventListener('close', () => { document.body.classList.remove('has-overlay'); if (this.opener) this.opener.focus(); });
        document.addEventListener('cart:updated', () => { if (this.dialog.open) this.close(); });
      }
      this.opener = opener;
      opener.classList.add('is-loading');
      try {
        const [html] = await Promise.all([
          fetch(`${productBase()}${encodeURIComponent(handle)}?section_id=quick-view`).then((r) => { if (!r.ok) throw new Error(); return r.text(); }),
          loadScript(PL.assets && PL.assets.product)
        ]);
        const content = $('product-info', parseHTML(html));
        if (!content) throw new Error();
        if (PL.assets && PL.assets.productCss && !document.querySelector(`link[href="${PL.assets.productCss}"]`)) {
          const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = PL.assets.productCss; document.head.appendChild(l);
        }
        const body = $('[data-qv-body]', this.dialog);
        body.innerHTML = '';
        body.appendChild(document.importNode(content, true));
        initWishlistButtons(body);
        if (PL.loyalty && PL.loyalty.render) PL.loyalty.render(body);
        this.dialog.showModal();
        document.body.classList.add('has-overlay');
        const first = $('.finder__option, .variant-picker input, select, [data-add-button]', body);
        /* Sans défilement : l'aperçu s'ouvre en haut (image, titre et bouton Fermer visibles) */
        if (first) first.focus({ preventScroll: true });
        this.dialog.scrollTop = 0;
      } catch (err) {
        window.location.href = `${productBase()}${encodeURIComponent(handle)}`;
      } finally {
        opener.classList.remove('is-loading');
      }
    },
    close() { if (this.dialog && this.dialog.open) this.dialog.close(); }
  };
  PL.quickView = QuickView;
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-quick-view]');
    if (!t || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    QuickView.open(t.dataset.quickView, t);
  });

  /* ---------- Fiche produit, téléphone : à côté de la photo (réglage « Téléphone : à côté de la photo », v85) ---------- */
  // Saveur choisie : suit le changement de variante ; au toucher, ramène aux pastilles de saveur
  document.addEventListener('variant:change', (e) => {
    const v = e.detail;
    if (!v || !v.options || !e.target || !e.target.querySelectorAll) return;
    $$('[data-pick-value]', e.target).forEach((el) => { el.textContent = v.options[Number(el.dataset.pickValue)] || ''; });
  });
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-mfill-flavor]');
    if (!b) return;
    const info = b.closest('product-info') || document;
    const fs = $(`variant-picker fieldset[data-option-index="${b.dataset.mfillFlavor}"]`, info);
    if (!fs) return;
    fs.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const inp = $('input:checked', fs) || $('input', fs);
    if (inp) { inp.focus({ preventScroll: true }); const lab = inp.closest('label') || inp.nextElementSibling; if (lab && lab.scrollIntoView) lab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' }); }
  });
  // Livraison et couronnes : reprises du bloc « Livraison estimée » et de la ligne des couronnes de la fiche (mêmes textes, à jour)
  const mfillDelivery = (box) => {
    const info = box.closest('product-info') || document;
    const ship = $('[data-mfill-ship]', box);
    const crowns = $('[data-mfill-crowns]', box);
    const flow = $('.delivery-flow.is-active:not([hidden])', info) || $('.delivery-flow:not([hidden])', info);
    if (ship) {
      const st = flow && $('[data-delivery-cutoff]', flow);
      const sp = flow && $('.delivery-flow__speed', flow);
      const a = st ? st.textContent.trim() : '';
      const c = sp ? sp.textContent.trim() : '';
      $('span', ship).innerHTML = [a && esc(a), c && `<span class="product__mfill-nowrap">${esc(c)}</span>`].filter(Boolean).join(' · ');
      ship.hidden = !(a || c);
    }
    if (crowns) {
      const line = $('.loyalty-line', info);
      const strong = line && !line.hidden && $('.loyalty-line__text strong', line);
      if (strong) {
        const x2 = line.classList.contains('loyalty-line--x2');
        $('span', crowns).innerHTML = `<strong>+${esc(strong.textContent.trim())}</strong>${x2 ? ' <i class="product__mfill-x2">×2</i>' : ''}`;
      }
      crowns.hidden = !strong;
    }
    box.hidden = (!ship || ship.hidden) && (!crowns || crowns.hidden);
  };
  const mfillInit = () => {
    $$('[data-mfill-delivery]').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      mfillDelivery(box);
      const info = box.closest('product-info') || document;
      const mo = new MutationObserver(() => mfillDelivery(box));
      $$('.delivery-flows, .delivery-flow, .loyalty-line', info).forEach((el) => mo.observe(el, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'class'] }));
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mfillInit); else mfillInit();

  /* ---------- Indice de fiabilité en % (fiche produit, snippets/product-fiabilite) ---------- */
  // La pastille ouvre sa fenêtre de détail ; clic sur le fond ou sur Fermer : fermeture (Échap : natif)
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-fiab-open]');
    if (opener) {
      const d = document.getElementById(opener.getAttribute('aria-controls'));
      if (d && typeof d.showModal === 'function' && !d.open) { d.showModal(); document.body.classList.add('has-overlay'); }
      return;
    }
    const d = e.target.closest('dialog.fiab');
    if (d && (e.target === d || e.target.closest('[data-fiab-close]'))) d.close();
  });
  document.addEventListener('close', (e) => {
    if (e.target.matches && e.target.matches('dialog.fiab') && !document.querySelector('dialog[open]')) document.body.classList.remove('has-overlay');
  }, true);

  /* ---------- Âge minimum à l'inscription ---------- */
  // Date vide, âge insuffisant ou case « 18 ans ou plus » non cochée : l'envoi est bloqué
  // (filet de sécurité si la validation native du navigateur est contournée).
  document.addEventListener('submit', (e) => {
    if (!e.target.querySelector) return;
    const input = e.target.querySelector('input[data-min-age]');
    const confirm = e.target.querySelector('input[data-age-confirm]');
    if (!input && !confirm) return;
    const block = (el, msg) => {
      e.preventDefault();
      el.setAttribute('aria-invalid', 'true');
      el.setCustomValidity(msg);
      el.reportValidity();
      el.addEventListener(el.type === 'checkbox' ? 'change' : 'input', () => { el.setCustomValidity(''); el.removeAttribute('aria-invalid'); }, { once: true });
    };
    if (input) {
      if (!input.value) return block(input, S.birthRequired || S.tooYoung);
      const birth = new Date(input.value);
      const min = parseInt(input.dataset.minAge, 10) || 18;
      const limit = new Date(); limit.setFullYear(limit.getFullYear() - min);
      if (isNaN(birth) || birth > limit) return block(input, S.tooYoung);
    }
    if (confirm && !confirm.checked) block(confirm, S.ageRequired || S.tooYoung);
  }, true);

  /* ---------- Utilitaires partagés avec les scripts de section ---------- */
  PL.u = { $, $$, debounce, parseHTML, storage, formatMoney, toast, trapFocus, esc, norm, num, fill, fetchProductData, productDataUrl, productBase, starsHTML, buyButton, isDesktop, reduceMotion, initWishlistButtons, applyDiscountCode, getCart, refreshCart };

  /* ---------- Divers ---------- */
  // Bascule générique (formulaires d'adresse, mot de passe oublié)
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-toggle-target]');
    if (!t) return;
    e.preventDefault();
    const target = document.getElementById(t.dataset.toggleTarget);
    if (!target) return;
    target.hidden = !target.hidden;
    // Tous les boutons qui ouvrent ce bloc reflètent son état ; un bouton « Annuler » placé dans le bloc rend le focus à l'ouvreur
    const openers = $$(`[data-toggle-target="${target.id}"]`).filter((b) => !target.contains(b));
    (openers.length ? openers : [t]).forEach((b) => b.setAttribute('aria-expanded', target.hidden ? 'false' : 'true'));
    if (!target.hidden) { const f = $(FOCUSABLE, target); if (f) f.focus(); }
    else if (target.contains(t) && openers[0]) openers[0].focus();
  });
  // Menu du compte en puces défilantes (mobile, tablette) : l'onglet actif reste visible
  $$('.account-nav ul').forEach((ul) => {
    const cur = $('[aria-current]', ul);
    if (cur && ul.scrollWidth > ul.clientWidth) ul.scrollLeft += cur.getBoundingClientRect().left - ul.getBoundingClientRect().left - 12;
  });
  if (window.location.hash === '#recover') { const r = $('#RecoverForm'); if (r) r.hidden = false; }

  // Confirmation avant suppression
  document.addEventListener('submit', (e) => {
    const f = e.target.closest('form[data-confirm]');
    if (f && !window.confirm(f.dataset.confirm)) e.preventDefault();
  });

  // Sélecteurs langue / pays : envoi au changement
  document.addEventListener('change', (e) => {
    const sel = e.target.closest('[data-localization-select]');
    if (sel && sel.form) sel.form.submit();
  });

  // Pays / régions dans les formulaires d'adresse
  const initProvinces = () => {
    $$('select[name="address[country]"]').forEach((country) => {
      const province = country.form && country.form.querySelector('select[name="address[province]"]');
      if (!province) return;
      if (country.dataset.default) country.value = country.dataset.default;
      const fill = () => {
        const opt = country.options[country.selectedIndex];
        let list = [];
        try { list = JSON.parse((opt && opt.dataset.provinces) || '[]'); } catch (err) { list = []; }
        province.innerHTML = list.map(([code, name]) => `<option value="${code}">${name}</option>`).join('');
        province.closest('.field').hidden = list.length === 0;
        if (province.dataset.default) province.value = province.dataset.default;
      };
      country.addEventListener('change', fill);
      fill();
    });
  };

  // Sommaire automatique des articles
  const initToc = () => {
    const toc = $('[data-toc]');
    const content = $('[data-article-content]');
    if (!toc || !content) return;
    const heads = $$('h2, h3', content);
    if (heads.length < 2) return;
    const list = $('[data-toc-list]', toc);
    let lastLi = null;
    heads.forEach((h, i) => {
      if (!h.id) h.id = `section-${i + 1}`;
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${h.id}`; a.textContent = h.textContent;
      li.appendChild(a);
      // Un H3 s'imbrique sous le H2 qui le précède : la numérotation de la liste reste continue (1, 2, 3)
      if (h.tagName === 'H3' && lastLi) {
        const sub = $(':scope > ul', lastLi) || lastLi.appendChild(document.createElement('ul'));
        sub.appendChild(li);
      } else {
        if (h.tagName === 'H3') li.className = 'toc-sub';
        else lastLi = li;
        list.appendChild(li);
      }
    });
    toc.hidden = false;
  };

  // Typographie des pages de contenu : pas de retour à la ligne après « e- » (e-liquide, e-mail)
  // ni avant un pronom (acceptez-vous), espace insécable avant ? ! : ; (pas de « ? » seul en début de ligne)
  const initTypo = () => {
    const main = $('main');
    if (!main || !/\btemplate-(page|article|blog)\b/.test(document.body.className)) return;
    const skip = /^(SCRIPT|STYLE|TEXTAREA|CODE|PRE|NOSCRIPT|OPTION)$/;
    const walker = document.createTreeWalker(main, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (n.parentElement && !skip.test(n.parentElement.tagName) && /[-?!:;]/.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT)
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((n) => {
      const t = n.nodeValue
        .replace(/(^|[^\p{L}\p{N}])(\p{L}{1,2})-(?=\p{L})/gu, '$1$2-⁠')
        .replace(/(\p{L})-(vous|nous|ils?|elles?|on|je|tu)(?!\p{L})/gu, '$1-⁠$2')
        .replace(/ ([?!:;])(?=\s|$)/g, ' $1');
      if (t !== n.nodeValue) n.nodeValue = t;
    });
  };

  // Apparition au défilement
  const initReveal = () => {
    if (!document.documentElement.classList.contains('has-anim') || reduceMotion || !('IntersectionObserver' in window)) return;
    if (window.Shopify && window.Shopify.designMode) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -60px 0px' });
    $$('main .section').forEach((s) => {
      if (s.getBoundingClientRect().top > window.innerHeight) { s.classList.add('reveal'); io.observe(s); }
    });
  };

  // Couronnes néon (snippets/neon-decor, couronne fidélité de l'en-tête) : allumage en clignotant au premier passage à
  // l'écran, une seule fois par visite (pl_neon) ; ensuite seulement la respiration lente (theme.css, « Animation des
  // couronnes néon »). Néon fixe sans le réglage Animations, si le visiteur réduit les animations et dans l'éditeur.
  /* Pied de page : accordions fermés sur téléphone, toujours ouverts au-delà (le titre n'y replie rien) */
  const footerMobile = window.matchMedia('(max-width: 749px)');
  const initFooterAcc = (root = document) => {
    $$('.footer__acc', root).forEach((d) => {
      d.open = !footerMobile.matches;
      if (d.dataset.accReady) return;
      d.dataset.accReady = '1';
      d.querySelector('summary').addEventListener('click', (e) => { if (!footerMobile.matches) e.preventDefault(); });
    });
  };
  footerMobile.addEventListener('change', () => initFooterAcc());

  /* Téléphone : longues listes et longs textes repliés, « Voir plus » / « Lire la suite » (data-more, data-more-list ou
     data-more-text, data-more-btn). Sans script ou sur ordinateur, tout reste affiché. */
  const initMore = (root = document) => {
    $$('[data-more]', root).forEach((box) => {
      if (box.dataset.moreReady) return;
      box.dataset.moreReady = '1';
      const btn = $('[data-more-btn]', box);
      const list = $('[data-more-list]', box);
      const text = $('[data-more-text]', box);
      if (!btn || !footerMobile.matches) return;
      if (list) {
        const items = [...list.children];
        const limit = parseInt(list.dataset.moreLimit || '8', 10);
        if (items.length <= limit) return;
        items.slice(limit).forEach((li) => li.classList.add('is-more-hidden'));
        btn.hidden = false;
        btn.addEventListener('click', () => {
          const first = items.find((li) => li.classList.contains('is-more-hidden'));
          items.forEach((li) => li.classList.remove('is-more-hidden'));
          btn.hidden = true;
          const link = first && $('a, button', first);
          if (link) link.focus();
        });
      } else if (text) {
        text.classList.add('is-collapsed');
        if (text.scrollHeight <= text.clientHeight + 8) { text.classList.remove('is-collapsed'); return; }
        btn.hidden = false;
        btn.addEventListener('click', () => { text.classList.remove('is-collapsed'); btn.hidden = true; text.setAttribute('tabindex', '-1'); text.focus({ preventScroll: true }); });
      }
    });
  };
  /* Remet une liste « Voir plus » entièrement affichée (filtre de la page des guides) */
  PL.u.moreReset = (box) => {
    if (!box) return;
    $$('.is-more-hidden', box).forEach((li) => li.classList.remove('is-more-hidden'));
    $$('[data-more-btn]', box).forEach((b) => { b.hidden = true; });
  };

  const initNeon = (root = document) => {
    const html = document.documentElement;
    const els = $$('.neon-decor, .header__loyalty', root);
    if (!els.length || html.classList.contains('neon-steady')) return;
    let seen = false;
    try { seen = sessionStorage.getItem('pl_neon') === '1'; } catch (e) {}
    if (seen || !html.classList.contains('has-anim') || reduceMotion || !('IntersectionObserver' in window) || (window.Shopify && window.Shopify.designMode)) {
      html.classList.remove('neon-ignite');
      html.classList.add('neon-steady');
      return;
    }
    html.classList.add('neon-ignite');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-lit'); io.unobserve(en.target); } });
      try { sessionStorage.setItem('pl_neon', '1'); } catch (e) {}
    }, { threshold: 0.15 });
    els.forEach((el) => io.observe(el));
  };

  // Rafraîchit l'état après un ajout au panier depuis l'éditeur ou une autre source
  document.addEventListener('shopify:section:load', (e) => { Wishlist.sync(); Compare.sync(); Loyalty.render(e.target); initProvinces(); initNeon(e.target); initFooterAcc(e.target); initMore(e.target); });

  const ready = () => {
    if ($('#MainCart')) fillCartRecos($('#MainCart'));
    WishlistSync.init();
    Wishlist.sync();
    Compare.sync();
    Badges.compat(document);
    Loyalty.init();
    initProvinces();
    initToc();
    initTypo();
    initReveal();
    initNeon();
    initFooterAcc();
    initMore();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready); else ready();
})();
